import {
    ACTIVATION_STRING_VALUE,
    Architecture,
    AVR_FUSE_VALUE,
    BootTemplatePayload,
    BOOT_BLOCK_SIZE_VALUE,
    CRC_POLYNOMIAL_VALUE,
    CRC_SEED_VALUE,
    CRC_SIZE_VALUE,
    DEVICE_TYPE_VALUES,
    EMPTY_DATA_VALUE,
    FTL_BOOLEAN_VALUE,
    FTL_NULL_VALUE,
    MEMORY_SIZE_VALUE,
    NVM_ACCESS_VALUE,
    RESET_BITMASK_VALUE,
    VERIFICATION_SCHEME_VALUE,
} from "./BootTemplateInterfaces";
import { architectureValidator, deviceValidator } from "./deviceValidator";
import * as ProjectProperty from "@microchip/project-properties/lib/project-properties";
import { decimal2Hex } from "./Helpers";
import * as Processor from "@microchip/scf-common/lib/Processor";
import { getKeys, values } from "../generated_module/src/Utils";
import { pin_attribs } from "@microchip/pin-standard";
import { MyComponentNames } from "../generated_module/src/types";

/**
 *
 * @param priority "high" | "low"
 * @param startAddress number the decimal value of the user interface input component
 * @param offsets interrupt vector data taken from BootMetadata
 * @returns the calculated offset of the iterrupt vector
 */
const getVectorDataForPriority = (
    priority: "high" | "low",
    startAddress: number,
    offsets: { high: number; low: number },
): number => {
    switch (priority) {
        case "high":
            return startAddress + offsets.high;

        case "low":
            return startAddress + offsets.low;
    }
};

/**
 * @function convertBool2MCC
 * @param val a boolean value that comes from a UI component
 * @returns SCF boolean values. true=>"Enabled" false=>"Disabled"
 */
const convertBool2MCC = (val: boolean | undefined): FTL_BOOLEAN_VALUE => {
    if (val) {
        return "Enabled";
    } else {
        return "Disabled";
    }
};

/**
 * @function getVerification
 * @param verificationStr string passed from the UI
 * @returns the template code used for injection
 */
const getVerification = (
    verificationStr: string | undefined,
): VERIFICATION_SCHEME_VALUE | FTL_NULL_VALUE => {
    switch (verificationStr) {
        case "Check Reset Vector":
            return "RESET_VECTOR_VERIFICATION";
        case "Check State Flag":
            return "STATUS_BYTE_VERIFICATION";
        case "CRC-16":
            return "CRC16_VERIFICATION";
        case "CRC-32":
            return "CRC32_VERIFICATION";
        case "Checksum":
            return "CHECKSUM_VERIFICATION";
        default:
            return "NotApplicable";
    }
};

/**
 * @function getCRCData
 * @param verify string representing the verification code
 * @returns BootTemplatePayload with only CRC information in it
 */
const getCRCData = (verify: VERIFICATION_SCHEME_VALUE): BootTemplatePayload => {
    let crcSeed: CRC_SEED_VALUE | FTL_NULL_VALUE = "NotApplicable";
    let crcPoly: CRC_POLYNOMIAL_VALUE | FTL_NULL_VALUE = "NotApplicable";
    let crcSize: CRC_SIZE_VALUE | FTL_NULL_VALUE = "NotApplicable";

    if (verify == "CRC32_VERIFICATION") {
        crcPoly = "0xEDB88320";
        crcSeed = "0xFFFFFFFF";
        crcSize = "4";
    } else if (verify === "CRC16_VERIFICATION") {
        crcPoly = "0x1021";
        crcSeed = "0xFFFF";
        crcSize = "2";
    }

    return {
        CRC_SEED_U: crcSeed,
        CRC_SIZE_U: crcSize,
        CRC_POLYNOMIAL_U: crcPoly,
        CRC_XOR_U: "0x00000000",
    };
};

/**
 * @function getIndicatorActivation
 * @param pinData a string representation of a JSON object
 * @returns "Active_Low" | "Active_High"
 */
const getIndicatorActivation = (pinData: string | undefined): ACTIVATION_STRING_VALUE => {
    let PinAttribs: any;
    let activation: ACTIVATION_STRING_VALUE = "Active_High";
    if (pinData != undefined) {
        // wrap in a try to prevent parser errors from breaking execution
        try {
            PinAttribs = JSON.parse(JSON.stringify(pinData));
            // Try to get attribs structure
            if (getKeys(PinAttribs).length !== 0) {
                values(PinAttribs).forEach((attrib: pin_attribs) => {
                    if (typeof attrib.high === "boolean" && attrib.high) {
                        activation = "Active_Low"; // this line is required to solve an async issue with the processor
                        return "Active_Low";
                    }
                });
            }
        } catch (e) {
            // do nothing but fail and return the default
        }
    }
    return activation;
};

/**
 * @function getCustomCname
 * @param pinComponentName name of the component that pinData came from
 * @param pinData the pin data that came from pinComponentName
 * @returns the custom name of the users pin
 */
const getCustomCname = (
    pinComponentName: MyComponentNames,
    pinData: string | undefined,
): string | undefined => {
    let PinAttribs: any;
    let cname = pinComponentName === "ioOutPin" ? "BL_INDICATOR" : "BL_ENTRY";
    if (pinData != undefined && pinData != "undefined") {
        try {
            PinAttribs = JSON.parse(JSON.stringify(pinData));
            // Try to get attribs structure out of pinData
            if (getKeys(PinAttribs).length !== 0) {
                values(PinAttribs).forEach((attrib: pin_attribs) => {
                    if (typeof attrib.cname === "string" && attrib.cname !== "") {
                        cname = attrib.cname; // this line is required to solve the async issue of the processor
                        return attrib.cname;
                    }
                });
            }
        } catch (e) {
            // do nothing but fail and return the default
        }
    }
    return cname;
};

/**
 * // TODO:
 * @param arch
 * @param deviceName
 * @returns
 */
const getNVMAccessType = (deviceName: string): NVM_ACCESS_VALUE => {
    if (deviceValidator.isTiny0(deviceName) || deviceValidator.isTiny1(deviceName)) {
        return "ROW_ACCESS";
    } else {
        return "BYTE_ACCESS";
    }
};

/**
 * @function getResetBits
 * @returns "RSTCTRL_SWRE_bm" | "RSTCTRL_SWRST_bm"
 */
const getResetBits = (
    arch: Architecture,
    deviceName: string,
): RESET_BITMASK_VALUE | FTL_NULL_VALUE => {
    if (architectureValidator.isAVR(arch)) {
        if (deviceValidator.isAVRXX(deviceName)) {
            //AVRDx
            return "RSTCTRL_SWRST_bm";
        } else {
            //ATtinyx
            return "RSTCTRL_SWRE_bm";
        }
    } else {
        //PIC18F and PIC16F
        return "NotApplicable";
    }
};

/**
 * 
 * @param arch 
 * @param deviceName 
 * @param fuseData 
 * @returns 
    // TODO: This value will most likely change. This should reflect the true size requirments of the code
 * 
 */
const getDeviceSpecificAlerts = (
    arch: Architecture,
    deviceName: string,
    alertData: { bootFuse: number; codeFuse: number; bootAddressValid: boolean },
): Processor.Alert[] => {
    const alerts: Processor.Alert[] = [];
    if (architectureValidator.isAVR(arch)) {
        if (deviceValidator.isAVRXX(deviceName)) {
            if (alertData.bootFuse < 0x6) {
                alerts.push({
                    type: Processor.AlertTypes.Warning,
                    text: "Recommended BOOTSIZE Range: Minimum Size - Maximum Size\n\nOptimization 0 -> 0x0E - 0x13\nOptimization 2 -> 0x06 - 0x0A",
                });
            } else {
                alerts.push({
                    type: Processor.AlertTypes.Hint,
                    text: "Recommended BOOTSIZE Range: Minimum Size - Maximum Size\n\nOptimization 0 -> 0x0E - 0x13\nOptimization 2 -> 0x06 - 0x0A",
                });
            }
        } else if (alertData.bootFuse < 0xb) {
            alerts.push({
                type: Processor.AlertTypes.Warning,
                text: "Recommended BOOTEND Range: Minimum Size - Maximum Size\n\nOptimization 0 -> 0x1C - 0x2D\nOptimization 2 -> 0x0B - 0x0D",
            });
        } else {
            alerts.push({
                type: Processor.AlertTypes.Hint,
                text: "Recommended BOOTEND Range: Minimum Size - Maximum Size\n\nOptimization 0 -> 0x1C - 0x2D\nOptimization 2 -> 0x0B - 0x0D",
            });
        }

        if (
            (alertData.bootFuse >= alertData.codeFuse || alertData.bootFuse == 0) &&
            alertData.codeFuse != 0x0
        ) {
            // default for ATtiny
            let codeName = "CODEEND";
            let bootName = "BOOTEND";
            // Set the new name if AVRXX family
            if (deviceValidator.isAVRXX(deviceName)) {
                codeName = "CODESIZE";
                bootName = "BOOTSIZE";
            }
            alerts.push({
                type: Processor.AlertTypes.Error,
                text: `The ${codeName} (${alertData.codeFuse}) should be a larger value than the ${bootName} (${alertData.bootFuse}). The current FUSE configuration will cause issues while programming the Bootloader`,
            });
        }
    } else if (
        architectureValidator.isPIC18(arch) ||
        architectureValidator.isPIC16(arch)
    ) {
        if (!alertData.bootAddressValid) {
            alerts.push({
                type: Processor.AlertTypes.Error,
                text: "Boot End Address cannot be set to 0x0",
            });
        }
    }

    return alerts;
};

/**
 *
 * @param arch
 * @param deviceName
 * @returns
 */
const getDeviceSpecificConfiguration = (
    arch: Architecture,
    addressRange: string | undefined,
): ProjectProperty.ProjectProperty[] => {
    // apply generic settings
    const configData: ProjectProperty.ProjectProperty[] = [
        {
            group: "HI-TECH-COMP",
            key: "extra-include-directories",
            value: "mcc_generated_files/bootloader;mcc_generated_files",
        },
    ];

    // include AVR specific config
    if (architectureValidator.isAVR(arch)) {
        configData.push({
            group: "AVR-GCC",
            key: "optimization-level",
            value: "-O2",
        } as ProjectProperty.ProjectProperty);
        configData.push({
            group: "HI-TECH-COMP",
            key: "optimization-level",
            value: "-O2",
        } as ProjectProperty.ProjectProperty);
    }
    // include all PIC spoecific settings
    else if (architectureValidator.isPIC16(arch) || architectureValidator.isPIC18(arch)) {
        configData.push({
            group: "HI-TECH-COMP",
            key: "optimization-level",
            value: "-O2",
        } as ProjectProperty.ProjectProperty);

        // Inject the start address range of the BL code automagically for the user
        if (addressRange && addressRange !== "0") {
            // PIC
            configData.push({
                group: "HI-TECH-LINK",
                key: "code-model-rom",
                value: addressRange,
            } as ProjectProperty.ProjectProperty);
        }
    }
    return configData;
};

/**
 * @function getFuse
 * @returns "Reserved_One_Byte" | "Reserved_Two_Byte"
 */
const getFuse = (
    arch: Architecture,
    deviceName: string,
): AVR_FUSE_VALUE | FTL_NULL_VALUE => {
    if (
        deviceValidator.isSupportedDevice(deviceName) &&
        architectureValidator.isAVR(arch)
    ) {
        if (deviceValidator.hasTCD0(deviceName)) {
            return "Reserved_One_Byte";
        }

        return "Reserved_Two_Byte";
    }
    return "NotApplicable";
};

/**
 * @function getAVRPage
 * @returns "Expanded" | "Classic"
 */
const getAVRPage = (
    deviceArch: Architecture,
    deviceName: string,
): MEMORY_SIZE_VALUE | FTL_NULL_VALUE => {
    if (architectureValidator.isAVR(deviceArch)) {
        if (deviceValidator.isAVRXX(deviceName)) {
            // AVRXX
            return "Expanded";
        } else {
            // ATtiny & ATmega
            return "Classic";
        }
    } else {
        // PIC16 / PIC18
        return "NotApplicable";
    }
};

/**
 * @function getChipType
 * @param deviceName string name of the chip
 * @returns Architecure keyword for device
 */
const getChipType = (arch: Architecture, deviceName: string): Architecture => {
    if (deviceValidator.isAVRXX(deviceName)) {
        return "AVR";
    } else if (
        deviceValidator.isTiny0(deviceName) ||
        deviceValidator.isTiny1(deviceName)
    ) {
        return "TINY";
    } else {
        return arch;
    }
};

/**
 * @function getEEPROMSize
 * @returns "Extended" | "Classic"
 */
const getEEPROMSize = (eeprom_size: number): string => {
    if (eeprom_size > 256) {
        return "Extended";
    } else {
        return "Classic";
    }
};

/**
 * @function getBootBlockSize
 * @returns BootBlockSizes.Extended | BootBlockSizes.Classic
 */
const getBootBlockSize = (deviceName: string): BOOT_BLOCK_SIZE_VALUE => {
    let blockSize: BOOT_BLOCK_SIZE_VALUE = "512bytes";
    if (deviceValidator.isTiny0(deviceName) || deviceValidator.isTiny1(deviceName)) {
        blockSize = "256bytes";
    }
    return blockSize;
};

/**
 * @function getNoAppCode
 * @returns "0x3FFF" | "0xFF"
 */
const getNoAppCode = (deviceArch: Architecture): EMPTY_DATA_VALUE => {
    return architectureValidator.isPIC16(deviceArch) ? "0x3FFF" : "0xFF";
};

/**
 * //TODO: This function will be removed in the next few srints
 */
const getRegisterNames = (deviceArch: Architecture): any => {
    return {
        EECON1_SFR: "NVMCON1",
        EECON2_SFR: architectureValidator.isPIC16(deviceArch) ? "NVMCON2" : "NVMLOCK",
        EEDATL_SFR: "NVMDATL",
        EEDATH_SFR: "NVMDATH",
        EEADRL_SFR: "NVMADRL",
        EEADRH_SFR: "NVMADRH",
        CFGS_BIT: "NVMREGS",
    };
};

/**
 * @function getSupportConfigWrite
 * @returns "Enabled" | "Disabled"
 */
const getSupportConfigWrite = (
    deviceArch: Architecture,
    deviceName: string,
): FTL_BOOLEAN_VALUE => {
    if (architectureValidator.isPIC18(deviceArch)) {
        return "Enabled";
    } else if (
        architectureValidator.isPIC16(deviceArch) &&
        (/PIC16F1.*/.test(deviceName) || /PIC16LF1.*/.test(deviceName))
    ) {
        return "Enabled";
    }

    return "Disabled";
};

/**
 * @function isPICKXXDeviceFamily
 * @returns "K83" | "K42" | "N/A"
 *
 * @todo this is probably not needed because PIC18FXXKXX is not supported by Melody yet
 */
const isPICKXXDeviceFamily = (deviceName: string): string | FTL_NULL_VALUE => {
    //if (deviceValidator.isSupportedDevice(deviceName)) {
    if (/.*K42.*/.test(deviceName)) {
        return "K42";
    } else if (/.*K83.*/.test(deviceName)) {
        return "K83";
    }
    //}
    return "NotApplicable";
};

/**
 * @function getDrainRXBUF
 * @returns "Enabled" | "Disabled"
 *
 * @todo with currently supported device in Melody this function will never return "Enabled"
 */
const getDrainRXBUF = (
    deviceArch: Architecture,
    deviceName: string,
): FTL_BOOLEAN_VALUE | FTL_NULL_VALUE => {
    if (architectureValidator.isPIC18(deviceArch)) {
        if (/PIC18F.*Q.*/.test(deviceName) || /PIC18F.*K42/.test(deviceName)) {
            return "Disabled";
        }
        return "Enabled";
    }
    return "NotApplicable";
};

/**
 * @function getDeviceType
 * @returns "AVR" | "PIC" | "N/A"
 */
const getDeviceType = (deviceArch: Architecture): DEVICE_TYPE_VALUES | FTL_NULL_VALUE => {
    if (architectureValidator.isAVR(deviceArch)) {
        return "AVR";
    } else if (architectureValidator.isPIC16(deviceArch)) {
        return "PIC";
    } else if (architectureValidator.isPIC18(deviceArch)) {
        return "PIC";
    }
    return "NotApplicable";
};

const PIC18_OFFSETS = {
    high: 0x08,
    low: 0x18,
};
const PIC16_OFFSETS = {
    high: 0x04,
    low: 0x04,
};
/**
 * @function getVector
 * @param startAddress the end address given by the user
 * @param code either "high" | "low" to distinguish what the operation is
 * @returns the new memory address based on configuration and input
 */
const getVector = (
    startAddress: number,
    code: "low" | "high",
    deviceArch: Architecture,
): string => {
    let result = 0;
    switch (code) {
        case "high":
            if (architectureValidator.isPIC16(deviceArch)) {
                result = getVectorDataForPriority("high", startAddress, PIC16_OFFSETS);
            } else if (architectureValidator.isPIC18(deviceArch)) {
                result = getVectorDataForPriority("high", startAddress, PIC18_OFFSETS);
            }
            break;
        case "low":
            if (architectureValidator.isPIC16(deviceArch)) {
                result = getVectorDataForPriority("low", startAddress, PIC16_OFFSETS);
            } else if (architectureValidator.isPIC18(deviceArch)) {
                result = getVectorDataForPriority("low", startAddress, PIC18_OFFSETS);
            }
            break;
    }
    if (result < 30) {
        return "NotApplicable";
    } else {
        return decimal2Hex(result);
    }
};

const getDeviceId = (deviceArch: Architecture): string => {
    if (architectureValidator.isPIC16(deviceArch)) {
        return "0x00";
    } else {
        return "0x3FFFFE";
    }
};

export const getters = {
    getChipType: getChipType,
    getResetBits: getResetBits,
    getFuse: getFuse,
    getAVRPage: getAVRPage,
    getEEPROMSize: getEEPROMSize,
    getBootBlockSize: getBootBlockSize,
    getRegisterNames: getRegisterNames,
    getNoAppCode: getNoAppCode,
    getSupportConfigWrite: getSupportConfigWrite,
    isPICKXXDeviceFamily: isPICKXXDeviceFamily,
    getDrainRXBUF: getDrainRXBUF,
    getDeviceType: getDeviceType,
    getInterruptVectorRange: getVector,
    getConfiguration: getDeviceSpecificConfiguration,
    getDeviceSpecificAlerts: getDeviceSpecificAlerts,
    getDeviceIdStart: getDeviceId,
    getNVMAccessType: getNVMAccessType,
    getCRCData: getCRCData,
    getCustomCname: getCustomCname,
    getIndicatorActivation: getIndicatorActivation,
    getVerification: getVerification,
    convertBool2MCC: convertBool2MCC,
};
